<script setup lang="ts">
import RatioContainer from '~/components/shared/ratioContainer.vue'
import LogoIcon from '~/components/ui/LogoIcon/LogoIcon.vue'

import { useSegmentTrack } from '~/composables/Segment/useSegmentTrack'
import { useProvideCoreFetch } from '~/composables/useProvideCoreFetch'

import { configToQs } from '~/helpers/configToQs'
import { trackEvent } from '~/helpers/LegacyTrackEvent'

import { Backgrounds } from '~/enums/CuratorWidgetBackgroundColor'
import {
  availableWidgetColorPalettes,
  availableWidgetFormat,
  availableWidgetRatios,
  MAX_WIDGET_WIDTH,
  MIN_WIDGET_WIDTH,
  RECTANGLE_WIDGET_HEIGHT,
} from '~/types/influencerWidget'

import type { WidgetParameters } from '~/types/influencerWidget'

definePageMeta({
  layout: 'barren',
})

const segmentTrack = useSegmentTrack()
const route = useRoute()
const { t, locale } = useI18n()
const query = route.query
const { coreFetch } = useProvideCoreFetch()

onMounted(() => setTracker)

function checkQueryFromType<T extends string>(
  str: string,
  availables: readonly T[],
): str is T {
  return availables.includes(str as T)
}

function flattenQuery(value?: string | (string | null)[]): string {
  if (typeof value !== 'string') value = value?.join() ?? ''

  return value
}

function sizeIsValid(
  size: number,
  format: WidgetParameters['format'],
): boolean {
  return (
    format === 'custom' &&
    !Number.isNaN(size) &&
    size >= MIN_WIDGET_WIDTH &&
    size <= MAX_WIDGET_WIDTH
  )
}

function parseWidgetParameters(): WidgetParameters {
  const simpleBooleanChecks = ['picture'] as const
  const out = {} as WidgetParameters

  // Check for simple boolean parameters, if they exist we count them as in
  for (const key of simpleBooleanChecks)
    out[key] = typeof query[key] !== 'undefined'

  // Check for custom text
  let { customText } = query
  customText = flattenQuery(customText?.toString()) ?? ''
  out.customText = !customText.length
    ? (t('influencer.widget.defaultCustomText') as string)
    : customText

  // Check for format
  let { format } = query
  format = flattenQuery(format?.toString())
  if (checkQueryFromType(format, availableWidgetFormat)) out.format = format
  else out.format = availableWidgetFormat[0]

  // Check for ratio
  let { ratio } = query
  ratio = flattenQuery(ratio?.toString())
  if (checkQueryFromType(ratio, availableWidgetRatios)) out.ratio = ratio
  else out.ratio = availableWidgetRatios[0]

  // Check for color palette
  let { color } = query
  color = flattenQuery(color?.toString())
  if (checkQueryFromType(color, availableWidgetColorPalettes)) out.color = color
  else out.color = availableWidgetColorPalettes[0]

  // Check for custom size
  const size = Number(query.size)
  if (sizeIsValid(size, out.format)) out.size = size
  else out.size = 0

  return out
}

const parameters = parseWidgetParameters()
const { data: coreData } = useAsyncData<{
  slug: string
  id: number
  entity: string
  picture: string
}>(
  () => {
    return coreFetch.$get(`/influencer/widget/${route.params.slug}/`)
  },
  {
    default: () => ({
      slug: '',
      id: 0,
      entity: '',
      picture: '',
    }),
  },
)

const link = computed<string>(() => {
  return `/${locale.value}/influencer/widget/redirect/${coreData.value.slug}/?${configToQs(
    {
      utm_source: 'widget',
      utm_medium: `widget_banner`,
      utm_campaign: coreData.value.slug,
      widget_id: coreData.value.id,
      from_widget: 'true',
    },
  )}`
})

// If parameters.ratio is not squared only the height becomes fixed and the width is capped
const ratioContainerComponent = computed(() =>
  parameters.ratio === 'square' ? markRaw(RatioContainer) : 'div',
)

const ratioContainerClasses = computed<string>(() => {
  const minWidth = `tw-min-w-[${MIN_WIDGET_WIDTH}]`
  const maxWidth =
    parameters.format === 'responsive'
      ? 'tw-max-w-full'
      : `tw-max-w-[${MAX_WIDGET_WIDTH}]`
  const width = sizeIsValid(parameters.size, parameters.format)
    ? `tw-w-[${parameters.size}]`
    : 'tw-w-full'
  const height =
    parameters.ratio === 'rectangle' ? `tw-h-[${RECTANGLE_WIDGET_HEIGHT}]` : ''

  return `${minWidth} ${maxWidth} ${width} ${height} ${parameters.ratio}`
})

const backgroundGradientStyle = computed<string>(() => {
  const background = Backgrounds[parameters.color]

  return `background: ${background}`
})

function emitEvent(): void {
  trackEvent(
    {
      category: 'Widget',
      action: 'click',
      slug: coreData.value.slug,
      entity: coreData.value.entity,
      ...parameters,
    },
    route,
  )
  segmentTrack('Curator Website - Widget Clicked', {
    curator_name: coreData.value.entity,
    curator_id: coreData.value.id,
  })
}

const fromWidgetCookie = useCookie('from_widget', {
  maxAge: 60 * 60 * 24 * 7,
})

function setTracker(): void {
  fromWidgetCookie.value = JSON.stringify(coreData.value.id)
}
</script>

<template>
  <div id="ignoreFontUpscale" class="ignoreFontUpscale">
    <a
      :href="link"
      target="_blank"
      :style="backgroundGradientStyle"
      :class="ratioContainerClasses"
      class="tw-relative tw-block tw-overflow-hidden"
      @click="emitEvent"
    >
      <LogoIcon class="floatingLogo" size-override="240px" />
      <component :is="ratioContainerComponent">
        <div class="mainBlock tw-p-xl">
          <div class="headBlock">
            <div v-if="parameters.picture" class="pictureBlock">
              <img
                class="tw-h-full tw-w-full tw-object-cover tw-object-center"
                :src="coreData.picture"
              />
            </div>
            <h2 class="ellipsis tw-text-h6 tw-capitalize tw-text-white">
              {{ coreData.entity }}
            </h2>
          </div>
          <p class="textBlock tw-text-body tw-font-medium tw-text-white">
            {{ parameters.customText }}
          </p>
        </div>
      </component>
    </a>
  </div>
</template>

<style scoped lang="scss">
.ellipsis {
  @apply tw-block;
}

.mainBlock {
  @apply tw-relative tw-h-full tw-w-full;
}

.floatingLogo {
  @apply tw-absolute tw-text-white;

  font-size: 240px;
  --tw-text-opacity: 0.2 !important;
}

.pictureBlock {
  @apply tw-relative tw-mr-3 tw-h-10 tw-w-10 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full;
}

.square {
  .mainBlock {
    @apply tw-grid tw-auto-rows-min tw-grid-cols-1 tw-content-center tw-justify-items-center;
  }

  .headBlock {
    @apply tw-mb-5;
  }

  .textBlock {
    @apply tw-text-center;

    line-height: 18px;
    -webkit-line-clamp: 3;

    @screen sm {
      line-height: 19px;
      -webkit-line-clamp: 6;
    }
  }

  .pictureBlock {
    @apply tw-mx-auto tw-mb-1;

    @screen 375 {
      @apply tw-mb-3 tw-h-20 tw-w-20;
    }
  }

  > .floatingLogo {
    --logo-size: 280px;

    font-size: var(--logo-size);
    left: var(--grid-2);
    bottom: calc(50% - (var(--logo-size) / 2));

    @screen 375 {
      left: var(--grid-3);
    }

    @screen sm {
      --logo-size: 480px;
      left: unset;
      right: calc(50% - (var(--logo-size)));
    }

    @screen 600 {
      --logo-size: 600px;
    }

    @screen md {
      --logo-size: 768px;
    }

    @screen lg {
      --logo-size: 1024px;
    }
  }
}

.textBlock {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @screen sm {
    max-width: 440px;
  }
}

.rectangle {
  .headBlock {
    @apply tw-hidden;

    @screen sm {
      @apply tw-relative tw-mb-3 tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-start tw-overflow-hidden;
    }
  }

  .textBlock {
    @apply tw-leading-5;

    -webkit-line-clamp: 5;

    @screen sm {
      -webkit-line-clamp: 2;
    }
  }

  > .floatingLogo {
    bottom: -18px;
    left: var(--grid-3);

    @screen sm {
      bottom: -36px;
    }

    @screen 600 {
      left: unset;
      right: 16px;
      bottom: calc(50% - 120px);
    }

    @screen md {
      right: 32px;
    }
  }
}
</style>
